import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import {
  CartesianGrid,
  Tooltip,
  XAxis,
  Legend,
  Bar,
  ResponsiveContainer,
  BarChart,
  YAxis,
  LabelList,
} from "recharts";
import { useReportFilter } from "../context/ReportFilterContext";
import { OperationType } from "../../../types/operationType";
import axios from "axios";
import { getValidToken } from "../../../utils/authUtils";
import { formatCurrency, formatDate } from "../../../utils/utils";
import { tableHeaderStyles, tableCellStyles } from "../../../utils/styles";
import { Loading } from "../../../components/Loading";

// Define interfaces for the API response
interface IncomeReportItem {
  Contract: string;
  Divisions: string;
  "Month-Year": string;
  "Contract Amount": string;
  "Billed Amount": string;
  "Difference Amount": string;
}

interface IncomeReportResponse {
  isSuccess: boolean;
  message: string;
  data: IncomeReportItem[];
}

const fetchIncomeOverallReport = async (
  timeFilter: string
): Promise<IncomeReportResponse> => {
  const token = await getValidToken();
  const url = `${process.env.REACT_APP_API_HOST}reports/super-project-income/${timeFilter}`;
  const response = await axios.get<IncomeReportResponse>(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

const IncomeOverallReport: React.FC = () => {
  const { filterState } = useReportFilter();
  const { timeFilter, customStartDate, customEndDate, selectedVehicles } =
    filterState;

  // State management
  const [reportData, setReportData] = useState<IncomeReportItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    const totalContractAmount = reportData.reduce(
      (sum, item) => sum + parseFloat(item["Contract Amount"]),
      0
    );
    const totalBilledAmount = reportData.reduce(
      (sum, item) => sum + parseFloat(item["Billed Amount"]),
      0
    );
    const totalDifferenceAmount = reportData.reduce(
      (sum, item) => sum + parseFloat(item["Difference Amount"]),
      0
    );

    return {
      totalContractAmount: totalContractAmount.toFixed(2),
      totalBilledAmount: totalBilledAmount.toFixed(2),
      totalDifferenceAmount: totalDifferenceAmount.toFixed(2),
    };
  }, [reportData]);

  // Prepare chart data by division
  const chartData = React.useMemo(() => {
    const divisionMap = new Map();

    reportData.forEach((item) => {
      const division = item.Divisions;
      if (!divisionMap.has(division)) {
        divisionMap.set(division, {
          division,
          contractAmount: 0,
          billedAmount: 0,
          differenceAmount: 0,
        });
      }

      const divisionData = divisionMap.get(division);
      divisionData.contractAmount += parseFloat(item["Contract Amount"]);
      divisionData.billedAmount += parseFloat(item["Billed Amount"]);
      divisionData.differenceAmount += parseFloat(item["Difference Amount"]);
    });

    return Array.from(divisionMap.values());
  }, [reportData]);

  useEffect(() => {
    const loadReportData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchIncomeOverallReport(timeFilter);
        if (response.isSuccess) {
          setReportData(response.data);
        } else {
          setError(response.message || "Failed to fetch report data");
        }
      } catch (err) {
        setError("An error occurred while fetching report data");
        console.error("Error fetching income overall report:", err);
      } finally {
        setLoading(false);
      }
    };

    loadReportData();
  }, [timeFilter]);
  return (
    <Box sx={{ position: "relative" }}>
      {/* Loading overlay */}
      {loading && <Loading fullScreen={false} message="Loading..." />}

      <Box sx={{}}>
        <Typography variant="h4" gutterBottom fontSize={16}>
          Income Overall Report
        </Typography>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: 1,
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                py: 1.5,
              }}
            >
              <Typography fontSize={12}>Total Contract Amount</Typography>
              {loading ? (
                <Skeleton variant="text" width="60%" height={20} />
              ) : (
                <Typography variant="h4" fontSize={15}>
                  {formatCurrency(summaryStats.totalContractAmount)}
                </Typography>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: 1,
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                py: 1.5,
              }}
            >
              <Typography fontSize={12}>Total Billed Amount</Typography>
              {loading ? (
                <Skeleton variant="text" width="60%" height={20} />
              ) : (
                <Typography variant="h4" fontSize={15} color="success.main">
                  {formatCurrency(summaryStats.totalBilledAmount)}
                </Typography>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: 1,
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                py: 1.5,
              }}
            >
              <Typography fontSize={12}>Total Difference Amount</Typography>
              {loading ? (
                <Skeleton variant="text" width="60%" height={20} />
              ) : (
                <Typography
                  variant="h4"
                  fontSize={15}
                  color={
                    parseFloat(summaryStats.totalDifferenceAmount) > 0
                      ? "warning.main"
                      : "success.main"
                  }
                >
                  {formatCurrency(summaryStats.totalDifferenceAmount)}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          {/* Chart Section */}
          {chartData.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom fontSize={16}>
                  Income Analysis by Division
                </Typography>
                {loading ? (
                  <Skeleton variant="rectangular" width="100%" height={400} />
                ) : (
                  <Box
                    sx={{
                      "&::-webkit-scrollbar": {
                        height: "6px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#f1f1f1",
                        borderRadius: "3px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#c1c1c1",
                        borderRadius: "3px",
                        "&:hover": {
                          backgroundColor: "#a8a8a8",
                        },
                      },
                      "&::-webkit-scrollbar-corner": {
                        backgroundColor: "#f1f1f1",
                      },
                      overflowX: "auto",
                      overflowY: "hidden",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: `${Math.max(chartData.length * 120, 600)}px`,
                        height: "400px",
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={chartData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="division"
                            fontSize={10}
                            interval={0}
                            angle={chartData.length > 4 ? -45 : 0}
                            textAnchor={chartData.length > 4 ? "end" : "middle"}
                            height={chartData.length > 4 ? 60 : 30}
                          />
                          <YAxis
                            fontSize={10}
                            tickFormatter={(value) => formatCurrency(value)}
                            tickCount={8}
                            domain={[
                              0,
                              (dataMax: number) => Math.ceil(dataMax * 1.1),
                            ]}
                            allowDataOverflow
                          />
                          <Tooltip
                            formatter={(value: any, name: any) => [
                              formatCurrency(value),
                              name,
                            ]}
                            labelStyle={{ fontSize: "12px" }}
                            contentStyle={{ fontSize: "11px" }}
                          />
                          <Legend />
                          <Bar
                            dataKey="billedAmount"
                            stackId="income"
                            name="Billed Amount"
                            fill="#82ca9d"
                            barSize={chartData.length < 6 ? 40 : 30}
                          />
                          <Bar
                            dataKey="differenceAmount"
                            stackId="income"
                            name="Difference Amount"
                            fill="#ff7c7c"
                            barSize={chartData.length < 6 ? 40 : 30}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
        {/* Division Performance Cards */}
        {chartData.length > 1 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: "1rem",
                  fontWeight: 700,
                  color: "#323130",
                  mb: 0.5,
                }}
              >
                Division Performance
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#605e5c",
                  fontSize: "0.8rem",
                }}
              >
                Top performing divisions by contract value
              </Typography>
            </Box>

            <Grid container spacing={1.5}>
              {chartData.map((item, index) => {
                const percentage =
                  chartData.length > 0
                    ? (item.contractAmount /
                        chartData.reduce(
                          (sum, d) => sum + d.contractAmount,
                          0
                        )) *
                      100
                    : 0;

                return (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 1.5,
                        bgcolor: "white",
                        border: "1px solid #e1dfdd",
                        borderRadius: 1,
                        position: "relative",
                        overflow: "hidden",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          elevation: 3,
                          transform: "translateY(-1px)",
                          borderColor: "#0078d4",
                        },
                      }}
                    >
                      {/* Ranking Badge */}
                      {index < 3 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            bgcolor:
                              index === 0
                                ? "#0078d4"
                                : index === 1
                                ? "#00bcf2"
                                : "#40e0d0",
                            color: "white",
                            borderRadius: "50%",
                            width: 18,
                            height: 18,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.6rem",
                            fontWeight: 700,
                          }}
                        >
                          {index + 1}
                        </Box>
                      )}

                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: "#323130",
                            letterSpacing: 0.3,
                            mb: 0.8,
                            lineHeight: 1.2,
                            textTransform: "uppercase",
                          }}
                        >
                          {item.division}
                        </Typography>

                        <Typography
                          variant="h4"
                          sx={{
                            fontSize: "1rem",
                            fontWeight: 700,
                            color: "#323130",
                            lineHeight: 1,
                            mb: 0.8,
                          }}
                        >
                          {formatCurrency(item.contractAmount)}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mt: "auto",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.65rem",
                              color: "#605e5c",
                              fontWeight: 500,
                            }}
                          >
                            Billed: {formatCurrency(item.billedAmount)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.65rem",
                              color: "#0078d4",
                              fontWeight: 600,
                            }}
                          >
                            {percentage.toFixed(1)}%
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        <Grid container spacing={3}>
          {/* Data Table */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom fontSize={16}>
                Income Details
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" width="100%" height={400} />
              ) : error ? (
                <Typography color="error" variant="body1">
                  Error: {error}
                </Typography>
              ) : reportData.length === 0 ? (
                <Typography
                  variant="h6"
                  sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 1 }}
                  align="center"
                >
                  No records found
                </Typography>
              ) : (
                <TableContainer sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeaderStyles}>Contract</TableCell>
                        <TableCell sx={tableHeaderStyles}>Division</TableCell>
                        <TableCell sx={tableHeaderStyles}>Month-Year</TableCell>
                        <TableCell sx={tableHeaderStyles} align="right">
                          Contract Amount
                        </TableCell>
                        <TableCell sx={tableHeaderStyles} align="right">
                          Billed Amount
                        </TableCell>
                        <TableCell sx={tableHeaderStyles} align="right">
                          Difference Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.map((row, index) => (
                        <TableRow
                          key={index}
                          sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}
                        >
                          <TableCell sx={tableCellStyles}>
                            {row.Contract}
                          </TableCell>
                          <TableCell sx={tableCellStyles}>
                            {row.Divisions}
                          </TableCell>
                          <TableCell sx={tableCellStyles}>
                            {row["Month-Year"]}
                          </TableCell>
                          <TableCell sx={tableCellStyles} align="right">
                            {formatCurrency(row["Contract Amount"])}
                          </TableCell>
                          <TableCell sx={tableCellStyles} align="right">
                            {formatCurrency(row["Billed Amount"])}
                          </TableCell>
                          <TableCell sx={tableCellStyles} align="right">
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              fontSize={12}
                              color={
                                parseFloat(row["Difference Amount"]) > 0
                                  ? "warning.main"
                                  : "success.main"
                              }
                            >
                              {formatCurrency(row["Difference Amount"])}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default IncomeOverallReport;
